import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import useSignup from "../../../services/useSignup";
import { ButtonLoader1 } from "../../../components/shared/ButtonLoaders";
import { SocialLogin } from "./components/SocialLogin";
import { PiEyeSlash, PiEyeLight } from "react-icons/pi";
import { useNavigate } from "react-router-dom";

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required").max(100, "Limit exceeded"),
  phone: Yup.string()
    .required("Mobile number is required")
    .max(100, "Limit exceeded"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required")
    .max(100, "Limit exceeded"),
  month: Yup.string().required("Month is required"),
  day: Yup.string().required("Day is required"),
  year: Yup.string().required("Year is required"),
  password: Yup.string()
    .required("Password is required")
    .max(100, "Limit exceeded"),
  confirm_password: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Confirm password is required"),
});

function Signup() {
  const [password, setPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState(false);
  const { Signup, loading } = useSignup();
  const navigate = useNavigate();

  const initialValues = {
    name: "",
    phone: "",
    email: "",
    month: "",
    day: "",
    year: "",
    password: "",
    confirm_password: "",
  };

  const { values, errors, handleChange, handleSubmit, touched } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const date_of_birth = `${values.year}-${values.month}-${values.day}`;

      // call the hook
      const { success } = await Signup({
        first_name: values.name.trim(),
        phone: values.phone.trim(),
        email: values.email.trim().toLowerCase(),
        password: values.password,
        last_name: "",
        date_of_birth,
      });

      // only redirect if success
      if (success) {
        navigate("/otp-verify", {
          state: {
            email: values.email.trim().toLowerCase(),
            password: values.password,
          },
        });
      }
    },
  });

  const months = [
    "01",
    "02",
    "03",
    "04",
    "05",
    "06",
    "07",
    "08",
    "09",
    "10",
    "11",
    "12",
  ];

  const days = Array.from({ length: 31 }, (_, i) =>
    String(i + 1).padStart(2, "0")
  );

  const years = Array.from(
    { length: 100 },
    (_, i) => new Date().getFullYear() - i
  );

  return (
    <>
      <div className="w-full min-h-[100vh] py-5">
        <div className="flex justify-between items-center px-12">
          <Link
            to="/"
            className="flex items-center gap-1 text-[24px] font-extrabold mb-2"
          >
            <img src="/assets/logo/logo.svg" alt="" />
            <h1 className="text-[#175635]">MedTag</h1>
          </Link>
          <Link to="/contact" className="text-primary">
            Contact
          </Link>
        </div>

        <div className="flex justify-center mt-8">
          <div className="flex flex-col justify-center items-center md:w-[500px] w-full px-4 md:px-0">
            <form onSubmit={handleSubmit} className="w-full">
              <p className="text-green-700 text-[20px] font-[700] text-center">
                Your Medical History at your grasp.
              </p>
              <p className="text-center text-sm text-gray-700 mt-1">
                Have an account?{" "}
                <Link
                  to="/login"
                  className="text-green-600 font-semibold underline"
                >
                  Sign in
                </Link>
              </p>

              <div className="w-full flex flex-col gap-3 mt-4">
                {/* Name */}
                <div>
                  <label className="font-normal text-[14px] text-[#0B4F2C]">
                    Name
                  </label>
                  <input
                    type="text"
                    placeholder="Name"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    className="w-full h-[40px] px-3 rounded-[10px] bg-[#EFEFF4] border border-[#DEE4ED] outline-none hover:border-primary focus:border-primary"
                  />
                  {errors.name && touched.name && (
                    <p className="text-red-700 text-xs mt-1">{errors.name}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="font-normal text-[14px] text-[#0B4F2C]">
                    Email Address
                  </label>
                  <input
                    type="text"
                    placeholder="Enter email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    className="w-full h-[40px] px-3 rounded-[10px] bg-[#EFEFF4] border border-[#DEE4ED] outline-none hover:border-primary focus:border-primary"
                  />
                  {errors.email && touched.email && (
                    <p className="text-red-700 text-xs mt-1">{errors.email}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="font-normal text-[14px] text-[#0B4F2C]">
                    Mobile Number
                  </label>
                  <input
                    type="text"
                    placeholder="Mobile number"
                    name="phone"
                    value={values.phone}
                    onChange={handleChange}
                    className="w-full h-[40px] px-3 rounded-[10px] bg-[#EFEFF4] border border-[#DEE4ED] outline-none hover:border-primary focus:border-primary"
                  />
                  {errors.phone && touched.phone && (
                    <p className="text-red-700 text-xs mt-1">{errors.phone}</p>
                  )}
                </div>

                {/* Date of Birth - Selects */}
                <div>
                  <label className="font-normal text-[14px] text-[#0B4F2C]">
                    Date of birth
                  </label>
                  <div className="flex gap-2">
                    {/* Month */}
                    <select
                      name="month"
                      value={values.month}
                      onChange={handleChange}
                      className="flex-1 h-[40px] px-3 rounded-[10px] bg-[#EFEFF4] border border-[#DEE4ED] outline-none hover:border-primary focus:border-primary"
                    >
                      <option value="">Month</option>
                      {months.map((m, i) => (
                        <option key={i} value={m}>
                          {new Date(0, i).toLocaleString("default", {
                            month: "long",
                          })}
                        </option>
                      ))}
                    </select>

                    {/* Day */}
                    <select
                      name="day"
                      value={values.day}
                      onChange={handleChange}
                      className="flex-1 h-[40px] px-3 rounded-[10px] bg-[#EFEFF4] border border-[#DEE4ED] outline-none hover:border-primary focus:border-primary"
                    >
                      <option value="">Day</option>
                      {days.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>

                    {/* Year */}
                    <select
                      name="year"
                      value={values.year}
                      onChange={handleChange}
                      className="flex-1 h-[40px] px-3 rounded-[10px] bg-[#EFEFF4] border border-[#DEE4ED] outline-none hover:border-primary focus:border-primary"
                    >
                      <option value="">Year</option>
                      {years.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                  {(errors.month || errors.day || errors.year) &&
                    (touched.month || touched.day || touched.year) && (
                      <p className="text-red-700 text-xs mt-1">
                        {errors.month || errors.day || errors.year}
                      </p>
                    )}
                </div>

                {/* Password */}
                <div>
                  <label className="font-normal text-[14px] text-[#0B4F2C]">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      placeholder="Enter your password"
                      name="password"
                      value={values.password}
                      onChange={handleChange}
                      type={password ? "text" : "password"}
                      className="w-full h-[40px] px-3 rounded-[10px] bg-[#EFEFF4] border border-[#DEE4ED] outline-none hover:border-primary focus:border-primary"
                    />
                    <div
                      className="h-full absolute top-0 right-2 cursor-pointer flex items-center justify-center text-center"
                      onClick={() => setPassword(!password)}
                    >
                      {password ? <PiEyeSlash /> : <PiEyeLight />}
                    </div>
                  </div>
                  {errors.password && touched.password && (
                    <p className="text-red-700 text-xs mt-1">
                      {errors.password}
                    </p>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="font-normal text-[14px] text-[#0B4F2C]">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      placeholder="Confirm Password"
                      name="confirm_password"
                      value={values.confirm_password}
                      onChange={handleChange}
                      type={confirmPassword ? "text" : "password"}
                      className="w-full h-[40px] px-3 rounded-[10px] bg-[#EFEFF4] border border-[#DEE4ED] outline-none hover:border-primary focus:border-primary"
                    />
                    <div
                      className="h-full absolute top-0 right-2 cursor-pointer flex items-center justify-center text-center"
                      onClick={() => setConfirmPassword(!confirmPassword)}
                    >
                      {confirmPassword ? <PiEyeSlash /> : <PiEyeLight />}
                    </div>
                  </div>
                  {errors.confirm_password && touched.confirm_password && (
                    <p className="text-red-700 text-xs mt-1">
                      {errors.confirm_password}
                    </p>
                  )}
                </div>
              </div>

              <button
                className="bg-primary hover:opacity-80 w-full h-[40px] text-white rounded-[22px] mt-6 flex justify-center items-center"
                type="submit"
                disabled={loading}
              >
                {loading ? <ButtonLoader1 /> : "Sign up"}
              </button>

              <div className="flex items-center mt-4">
                <input
                  type="checkbox"
                  id="terms"
                  className="accent-green-700 w-5 h-5 mr-2"
                  required
                />
                <label htmlFor="terms" className="text-sm text-gray-700">
                  I agree to the{" "}
                  <span className="text-green-700 font-semibold underline cursor-pointer">
                    Terms of Service
                  </span>
                </label>
              </div>
            </form>

            <p className="text-sm my-4 text-primary text-center">
              Or register with
            </p>
            <SocialLogin />
          </div>
        </div>
      </div>
    </>
  );
}

export default Signup;
